import { useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { Accordion, Box, Text } from '@opendoor/bricks-next';
import type {
  MarketplacePropertyListFragment,
  MarketplacePropertySearchFragment,
} from '__generated__/athena';
import { fetchMainSiteHeader } from 'cms';
import { createContentfulClient } from 'cms/api';
import { getAthenaClient } from 'components/api';
import { COSMOS_URL } from 'components/globals';
import { MapMarker } from 'components/marketplace/map/MapMarker';
import { MarketplaceMap } from 'components/marketplace/map/MarketplaceMap';
import type { MapMarkerProps, TopMarker } from 'components/marketplace/map/types';
import { SimilarListings } from 'components/marketplace/property';
import { Schools } from 'components/marketplace/property/Schools';
import MarketplaceMapLayout from 'components/properties/shared/MarketplaceMapLayout';
import { Divider } from 'components/shared/Divider';
import type { ISiteHeader } from 'declarations/contentful';
import type {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from 'declarations/shared/serverSideProps';
import type { MapRef } from 'react-map-gl';

const Page: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  entry,
  header,
  marketMetrics,
  properties,
  propertiesForMap,
  slug,
}) => {
  const [topMarker, setTopMarker] = useState<TopMarker | null>(null);
  const mapRef = useRef<MapRef>(null);

  const markerRefs = useRef<ReactNode | null>(null);
  const homesForSaleMarkers = useMemo(() => {
    markerRefs.current = propertiesForMap?.results?.map((property, idx) => {
      const handleClick = () => {
        window.open(
          `${COSMOS_URL}/properties/${property?.slug ?? '_'}/aid_${
            property?.addressToken
          }?utm_source=neighborhood-guide&utm_term=${slug}`,
        );
      };

      const lat = property.location?.lat;
      if (lat === undefined) {
        return undefined;
      }

      const lon = property.location?.lon;
      if (lon === undefined) {
        return undefined;
      }

      const mergedProperty = {
        ...property,
        addressToken: property.addressToken || '',
        isLiked: false,
        location: {
          lat,
          lon,
        },
      };

      return (
        <MapMarker
          key={`${property.addressToken}`}
          property={mergedProperty}
          onClick={handleClick}
          moveToTop={(hasFocus: boolean) => {
            setTopMarker({ index: idx, address: property.addressToken || '', hasFocus });
          }}
          setFocused={topMarker?.hasFocus}
        />
      );
    });
    return markerRefs.current;
  }, [propertiesForMap, properties]);

  return (
    <MarketplaceMapLayout header={header}>
      <Box
        flexDirection="row-reverse"
        backgroundColor="$backgroundPrimary"
        width="100%"
        overflow="hidden"
        height="100vh"
        $smallerThanMD={{
          flexDirection: 'column',
        }}
      >
        <Box
          $largerThanSM={{
            flexBasis: '50%',
          }}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          overflowY="scroll"
          width="100%"
          height="100%"
          pb={80}
          px={48}
        >
          <Text tag="h2" typography="$labelMedium" color="$contentTertiary" pt={20}>
            Neighborhood Guide
          </Text>
          <Text typography="$headerMedium" tag="h1" pt={10}>
            {entry.fields.neighborhood}
          </Text>
          <Text typography="$bodySmall" tag="h2" pt={30}>
            {entry.fields.overview}
          </Text>
          <Box
            flexDirection="row"
            justifyContent="space-between"
            $smallerThanLG={{ flexDirection: 'column' }}
          >
            {entry.fields.pros_cons.pros.length > 0 && (
              <Box width="48%" $smallerThanLG={{ width: '100%' }}>
                <Text typography="$labelMedium" tag="p" mt={24} mb={12}>
                  What to love
                </Text>
                <Box tag="ul" $platform-web={{ display: 'block' }} paddingLeft="$8x" margin={0}>
                  {entry.fields.pros_cons.pros.map((pro: string) => (
                    <Box tag="li" key={pro} $platform-web={{ display: 'list-item' }}>
                      <Text typography="$bodySmall" tag="p">
                        {pro}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {entry.fields.pros_cons.cons.length > 0 && (
              <Box width="48%" $smallerThanLG={{ width: '100%' }}>
                <Text typography="$labelMedium" tag="p" mt={24} mb={12}>
                  What to consider
                </Text>
                <Box tag="ul" $platform-web={{ display: 'block' }} paddingLeft="$8x" margin={0}>
                  {entry.fields.pros_cons.cons.map((con: string) => (
                    <Box tag="li" key={con} $platform-web={{ display: 'list-item' }}>
                      <Text typography="$bodySmall" tag="p">
                        {con}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          {/** Market Trends */}
          <Divider mb="$8x" mt="$24x" />
          <Accordion
            items={[
              {
                title: 'Market Trends',
                description: (
                  <Box>
                    <Text typography="$bodySmall" tag="p">
                      {entry.fields.real_estate_market_trends}
                    </Text>
                    <Box backgroundColor="$backgroundSecondary" p={24} mt={24} borderRadius="$8x">
                      <Box flexDirection="row" justifyContent="space-between">
                        <Text typography="$headerMedium" tag="p" color="$contentPrimary">
                          $
                          {marketMetrics?.medianSalePrice.toLocaleString('en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits:
                              marketMetrics?.medianSalePrice >= 1_000_000 ? 2 : 0,
                            notation: 'compact',
                            compactDisplay: 'short',
                          })}
                        </Text>
                        <Text color="$contentPrimary" typography="$labelSmall" tag="span">
                          {marketMetrics?.medianSalePriceMom &&
                          marketMetrics?.medianSalePriceMom < 0
                            ? '↘'
                            : '↗'}{' '}
                          {Math.round(marketMetrics?.medianSalePriceMom * 100)}%
                        </Text>
                      </Box>
                      <Text typography="$labelSmall" color="$contentSecondary" tag="p" mt={12}>
                        Median sale price
                      </Text>
                    </Box>
                    <Box flexDirection="row" justifyContent="space-between">
                      <Box
                        backgroundColor="$backgroundSecondary"
                        p={24}
                        mt={24}
                        borderRadius="$8x"
                        width="48%"
                      >
                        <Box flexDirection="row" justifyContent="space-between">
                          <Text typography="$headerMedium" tag="p" color="$contentPrimary">
                            {Math.round(marketMetrics?.daysOnMarket)}
                          </Text>
                          <Text color="$contentPrimary" typography="$labelSmall" tag="span">
                            {marketMetrics?.daysOnMarketMom && marketMetrics?.daysOnMarketMom < 0
                              ? '↘'
                              : '↗'}{' '}
                            {Math.round(marketMetrics?.daysOnMarketMom * 100)}%
                          </Text>
                        </Box>
                        <Text typography="$labelSmall" color="$contentSecondary" tag="p" mt={12}>
                          Average days on market
                        </Text>
                      </Box>
                      <Box
                        backgroundColor="$backgroundSecondary"
                        p={24}
                        mt={24}
                        borderRadius="$8x"
                        width="48%"
                      >
                        <Box flexDirection="row" justifyContent="space-between">
                          <Text typography="$headerMedium" tag="p" color="$contentPrimary">
                            {marketMetrics?.homesSold}
                          </Text>
                          <Text color="$contentPrimary" typography="$labelSmall" tag="span">
                            {marketMetrics?.homesSoldMom && marketMetrics?.homesSoldMom < 0
                              ? '↘'
                              : '↗'}{' '}
                            {Math.round(marketMetrics?.homesSoldMom * 100)}%
                          </Text>
                        </Box>
                        <Text typography="$labelSmall" color="$contentSecondary" tag="p" mt={12}>
                          Homes sold
                        </Text>
                      </Box>
                    </Box>
                    <Text
                      mt={24}
                      tag="p"
                      typography="$labelXsmall"
                      color="$contentStateInactivePrimary"
                    >
                      Market trends are based on the previous 4 weeks of data in the{' '}
                      {entry.fields.neighborhood_zip_code} zip code.
                    </Text>
                  </Box>
                ),
              },
            ]}
            variant="medium"
            size="xlarge"
            isFullWidth
            defaultOpen
          />
          <Divider mb="$8x" mt="$8x" />
          <Schools
            schools={entry.fields.school_data}
            summary={entry.fields.schools_and_education}
            disclaimer="Data provided by GreatSchools"
          />
          <Divider my="$8x" />
          {/** Safety */}
          <Accordion
            items={[
              {
                title: 'Safety',
                description: (
                  <Box>
                    <Text typography="$bodySmall" tag="p">
                      {entry.fields.safety}
                    </Text>
                    <Box flexDirection="row" justifyContent="space-between" mt={24}>
                      <Text tag="p" typography="$bodySmall" color="$contentPrimary">
                        Overall Crime Risk
                      </Text>
                      <Text tag="p" typography="$labelSmall" color="$contentPrimary">
                        {entry.fields.crime_data?.overall_crime
                          ? `${entry.fields.crime_data.overall_crime}`
                          : 'N/A'}
                      </Text>
                    </Box>
                    <Divider my="$8x" />
                    <Box flexDirection="row" justifyContent="space-between">
                      <Text tag="p" typography="$bodySmall" color="$contentPrimary">
                        Violent Crime Risk
                      </Text>
                      <Text tag="p" typography="$labelSmall" color="$contentPrimary">
                        {entry.fields.crime_data?.violent_crime
                          ? `${entry.fields.crime_data.violent_crime}`
                          : 'N/A'}
                      </Text>
                    </Box>
                    <Divider my="$8x" />
                    <Box flexDirection="row" justifyContent="space-between">
                      <Text tag="p" typography="$bodySmall" color="$contentPrimary">
                        Property Crime Risk
                      </Text>
                      <Text tag="p" typography="$labelSmall" color="$contentPrimary">
                        {entry.fields.crime_data?.property_crime
                          ? `${entry.fields.crime_data.property_crime}`
                          : 'N/A'}
                      </Text>
                    </Box>
                    <Text
                      typography="$labelXsmall"
                      tag="p"
                      color="$contentStateInactivePrimary"
                      mt={24}
                    >
                      Provided by Ovogo.com
                    </Text>
                  </Box>
                ),
              },
            ]}
            variant="medium"
            size="xlarge"
            isFullWidth
          />
          <Divider my="$8x" />
          <Accordion
            items={[
              {
                title: 'Transportation',
                description: (
                  <Box>
                    <Text typography="$bodySmall" tag="p">
                      {entry.fields.transportation}
                    </Text>
                    <Box flexDirection="column" mt={24}>
                      {Object.keys(entry.fields.public_transit)?.map((category: string, index) => (
                        <Box key={category}>
                          <Box
                            flexDirection="column"
                            justifyContent="space-between"
                            mb={
                              index === Object.keys(entry.fields.public_transit).length - 1 ? 24 : 0
                            }
                          >
                            <Text
                              tag="p"
                              typography="$bodySmall"
                              color="$contentSecondary"
                              textTransform="capitalize"
                            >
                              {category.split('_').join(' ')}
                            </Text>
                            <Text tag="p" typography="$bodySmall" color="$contentPrimary">
                              {entry.fields.public_transit?.[category]}
                            </Text>
                          </Box>
                          {index !== Object.keys(entry.fields.public_transit).length - 1 && (
                            <Divider my="$8x" />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ),
              },
            ]}
            variant="medium"
            size="xlarge"
            isFullWidth
          />
          <Divider my="$8x" />
          <Accordion
            items={[
              {
                title: 'Entertainment and recreation',
                description: (
                  <Box>
                    <Text typography="$bodySmall" tag="p">
                      {entry.fields.entertainment_and_recreation}
                    </Text>
                    <br />
                    <Text typography="$bodySmall" tag="p">
                      {entry.fields.community_events}
                    </Text>
                  </Box>
                ),
              },
            ]}
            variant="medium"
            size="xlarge"
            isFullWidth
          />
          <Divider my="$8x" />
          <Accordion
            items={[
              {
                title: 'Parks',
                description: (
                  <Box>
                    <Text typography="$bodySmall" tag="p">
                      {entry.fields.parks_and_green_spaces}
                    </Text>
                  </Box>
                ),
              },
            ]}
            variant="medium"
            size="xlarge"
            isFullWidth
          />
          <Divider my="$8x" />
          <Box pt={34}>
            <SimilarListings
              listings={properties?.results || []}
              title={`Available homes near ${entry.fields.neighborhood}`}
              utmSource="neighborhood-guide"
              utmTerm={slug as string}
            />
          </Box>
        </Box>
        <Box
          height="100vh"
          $smallerThanMD={{
            display: 'none',
          }}
          $largerThanSM={{
            flexBasis: '50%',
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            overflowY: 'scroll',
          }}
        >
          <MarketplaceMap
            mapRef={mapRef}
            handleOnClick={() => {}}
            handleOnMapMove={() => {}}
            center={[entry.fields.location?.lat, entry.fields.location?.lon]}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            mapMarkers={homesForSaleMarkers as ReactElement<MapMarkerProps>[]}
            zoom={11}
            dragPan={false}
            showGeoLocationControls={false}
          />
        </Box>
      </Box>
    </MarketplaceMapLayout>
  );
};

type ContentfulEntry = {
  fields: {
    location: {
      lat: number;
      lon: number;
    };
    neighborhood: string;
    overview: string;
    pros_cons: {
      pros: string[];
      cons: string[];
    };
    real_estate_market_trends: string;
    schools_and_education: string;
    safety: string;
    crime_data: {
      overall_crime: string;
      violent_crime: string;
      property_crime: string;
    };
    transportation: string;
    public_transit: { [k: string]: string };
    entertainment_and_recreation: string;
    restaurants_and_shopping: string;
    community_events: string;
    local_businesses: string;
    parks_and_green_spaces: string;
    tips_for_home_buyers: string;
    school_data: {
      name: string;
      latitude: number;
      longitude: number;
    }[];
    neighborhood_zip_code: string;
  };
};

type ContentfulEntries = {
  items: ContentfulEntry[];
};

export const getServerSideProps: CacheSafeGetServerSideProps<{
  entry: ContentfulEntry;
  header: ISiteHeader | null;
  properties?: MarketplacePropertyListFragment | null;
  propertiesForMap?: MarketplacePropertySearchFragment | null;
  marketMetrics: any;
  slug: string | string[] | undefined;
}> = async (context) => {
  const SEARCH_RADIUS_METERS = 4000;
  const TRENDS_LOOKBACK_WEEKS = 4;

  const slug = context.query.slug;
  if (!slug) {
    return {
      notFound: true,
    };
  }
  const client = createContentfulClient();

  const [entry, header] = await Promise.all([
    client.getEntries({
      content_type: 'neighborhoodGuide',
      include: 3,
      'fields.slug': slug,
      limit: 1,
    }) as Promise<ContentfulEntries>,
    fetchMainSiteHeader().catch(() => null),
  ]);

  if (!entry?.items?.length) {
    return {
      notFound: true,
    };
  }

  const nearbyZones = await getAthenaClient(context.req.headers).SearchZonesByLocation({
    latitude: entry.items[0].fields.location.lat,
    longitude: entry.items[0].fields.location.lon,
    searchDistance: SEARCH_RADIUS_METERS,
  });
  const zoneIds = nearbyZones?.geoDataStore?.searchZonesByLocation?.zones.map((zone) => zone.uuid);

  const propertyList = await getAthenaClient().GetMarketplacePropertyList({ page: 0, zoneIds });
  const propertySearch = await getAthenaClient().GetMarketplacePropertySearch({ zoneIds });
  const marketMetrics = await getAthenaClient().HistoricalMarketMetricsQuery({
    zip: entry.items[0].fields.neighborhood_zip_code,
    numWeeks: TRENDS_LOOKBACK_WEEKS,
  });

  return {
    props: {
      entry: entry.items[0],
      header,
      properties: propertyList?.marketplace?.propertyList,
      propertiesForMap: propertySearch?.marketplace?.propertySearch,
      marketMetrics: marketMetrics?.sellerDashboardMetrics?.getHistoricalMarketMetrics,
      slug,
    },
  };
};

Page.displayName = 'NeighborhoodGuidePage';
export default Page;
